class ConfiguracaoIcms {
    static get defaults() {
        return { atividades: [], perfis: [], aplicacoes: [] }
    }

    constructor(gql, $scope, parametrosTributarios) {
        this.apply = fn => $scope.$apply(fn)
        this.gql = gql
        this.atividades = []
        this.parametrosTributarios = parametrosTributarios

        $scope.$watch(
            () => this.atividade,
            atividade => {
                if (atividade) {
                    this.model.atividades = [...this.model.atividades, atividade].sortBy(atividade => atividade.descricao)
                    this.atividade = null
                    this.form.atividade.$setValidity('required', this.model.atividades.length > 0)
                }
            }
        )

        $scope.$watch(
            () => this.perfil,
            perfil => {
                if (perfil) {
                    this.model.perfis.push({ perfil, configuracoes: { numeroTermo: "", cnpjFornecedores: [] } })
                    this.perfil = null
                }
            }
        )

        $scope.$watch(() => JSON.stringify(this.model), () => {
            if (this.model) {
                const icms = this.formatModel(this.model)

                if (icms) {
                    this.onChange({ icms })
                }
            }
        })
    }

    $onInit() {
        this.promise = this
            .loadParametrosIcms()
            .then(({ atividades, regimesTributarios }) => {
                this.atividades = atividades.sortBy((atividade) => atividade.descricao)
                this.regimesTributarios = regimesTributarios
                this.model = this.parseModel(this.icms)
            })
    }

    $onChanges({ icms }) {
        if (icms && icms.currentValue) {
            this.form && this.form.$setPristine()
            this.model = this.parseModel(icms.currentValue)
        }
    }

    loadParametrosIcms() {
        const query = gql`
            query ParametrosIcms {
                parametrosIcms {
                    atividades { 
                        id
                        descricao
                        desativado 
                        aplicacoes {
                            id
                            padrao
                            aplicacao {
                                id
                                nome
                            }                            
                        }
                    }
                    regimesTributarios { 
                        id 
                        nome
                        calculaDa
                        calculaAt
                        retemCredito
                        estado {
                            id
                            sigla
                        }
                        perfis {
                            id
                            nome
                            requerNumeroTermo
                            requerCnpjFornecedores
                            industria
                        }
                    }
                }
            }
        `

        return this.gql
            .query(query)
            .then(({ parametrosIcms }) => {
                let { atividades, regimesTributarios } = parametrosIcms

                atividades = atividades
                    .sortBy(a => a.descricao)
                    .map(at => ({
                        ...at,
                        id: at.id,
                        aplicacoes: at.aplicacoes.map(ap => ({ ...ap, id: ap.id }))
                    }))

                regimesTributarios = regimesTributarios.map(r => ({
                    ...r,
                    id: r.id,
                    perfis: r.perfis.map(p => ({ ...p, id: p.id }))
                }))

                return { atividades, regimesTributarios }
            })
    }

    removeAtividade(atividade) {
        this.model.atividades = this.model.atividades.reject(at => at.id === atividade.id)
    }

    removePerfil(perfil) {
        this.model.perfis = this.model.perfis.reject(p => p.perfil.id === perfil.perfil.id)
    }

    addCnpjFornecedor(perfil) {
        const { configuracoes, cnpjFornecedor } = perfil
        const { cnpjFornecedores } = configuracoes

        if (cnpjFornecedor) {
            configuracoes.cnpjFornecedores = [...cnpjFornecedores, cnpjFornecedor].uniq()
            perfil.cnpjFornecedor = null
        }
    }

    removeCnpjFornecedor({ configuracoes }, cnpj) {
        configuracoes.cnpjFornecedores = configuracoes.cnpjFornecedores.reject(val => val === cnpj)
    }

    onCnpjKeyDown($event, perfil) {
        const { key, target } = $event
        const { configuracoes } = perfil
        const length = configuracoes.cnpjFornecedores.length

        if (length && key === "Backspace" && target.value === '') {
            configuracoes.cnpjFornecedores = configuracoes.cnpjFornecedores.slice(0, length - 1)
        }

        if (["Enter"].includes(key)) {
            this.addCnpjFornecedor(perfil)
            $event.preventDefault()
        }
    }

    get filteredAtividades() {
        if (this.atividades && this.model.atividades) {
            return this.atividades
                .reject(atividade => this.model.atividades.find(at => atividade.id === at.id))
        }

        return []
    }

    get filteredPerfis() {
        if (this.model.regimeTributario) {
            return this.model.regimeTributario.perfis
                .reject(perfil => this.model.perfis.find(p => p.perfil.id === perfil.id))
        }

        return []
    }

    get aplicacoes() {
        if (this.model && this.model.atividades) {
            return this.model.atividades
                .flatMap(atividade => atividade.aplicacoes)
                .filter(aplicacao => !aplicacao.padrao)
                .map(({ aplicacao }) => aplicacao)
                .uniqBy(aplicacao => aplicacao.id)
        }

        return []
    }

    get regimeTributarioPerfis() {
        const { regimeTributario } = this.model

        return regimeTributario ? regimeTributario.perfis : []
    }

    formatModel(icms) {
        let { atividades, aplicacoes, perfis, regimeTributario, vigencia, ...rest } = icms

        if (atividades && aplicacoes) {
            const regimeTributarioId = regimeTributario ? regimeTributario.id : null
            const atividadesIds = atividades.map(at => at.id)
            const aplicacoesIds = aplicacoes
                .filter(ap => this.aplicacoes.find(({ id }) => ap === id))

            perfis = perfis
                .filter(p => this.regimeTributarioPerfis.find(({ id }) => id === p.perfil.id))
                .map(({ perfil, configuracoes }) => ({
                    configuracoes,
                    perfilId: perfil.id
                }))

            return {
                ...rest,
                regimeTributarioId,
                atividadesIds,
                aplicacoesIds,
                perfis,
                vigencia: {
                    from: vigencia ? this.toDateString(vigencia.inicial) : null,
                    to: vigencia ? this.toDateString(vigencia.final) : null
                }
            }
        }
    }

    parseModel(icms) {
        if (icms && this.regimesTributarios) {
            let { atividadesIds, aplicacoesIds, perfis, regimeTributarioId, vigencia, ...rest } = icms

            const regimeTributario = this.regimesTributarios.find(({ id }) => id === regimeTributarioId)

            perfis = perfis.map(({ perfilId, configuracoes }) => {
                return {
                    configuracoes,
                    perfil: regimeTributario.perfis.find(perfil => perfil.id === perfilId)
                }
            })

            const atividades = atividadesIds
                .map(id => this.atividades.find(at => at.id === id))
                .filter(at => at)
                .sortBy(atividade => atividade.descricao)

            return {
                ...rest,
                regimeTributario,
                atividades,
                aplicacoes: aplicacoesIds,
                perfis,
                vigencia: {
                    inicial: vigencia.from && this.fromDateString(vigencia.from),
                    final: vigencia.to && this.fromDateString(vigencia.to)
                }
            }
        }

        return null
    }

    onVigenciaChange() {
        if (this.model.vigencia) {
            this.form.vigencia.$setValidity('overlap', true)
        }
    }

    toDateString(date) {
        return date ? date.toISODateWithoutTimezone() : null
    }

    fromDateString(str) {
        return new Date(str.split("-"))
    }

    submit() {
        this.model.perfis.forEach(({ perfil, configuracoes: { cnpjFornecedores } }) => {
            const field = this.form[`cnpj${perfil.id}`]

            field && field.$setValidity('cnpj', !perfil.requerCnpjFornecedores || cnpjFornecedores.length > 0)
        })

        this.form.atividade.$setValidity('required', this.model.atividades.length > 0)

        if (!this.form.$valid) return;

        const icms = this.formatModel(this.model)

        if (this.model.id) {
            this.updateFilialIcms(icms)
        } else {
            this.createFilialIcms(icms)
        }
    }

    createFilialIcms(icms) {
        this.promise = this.parametrosTributarios
            .createFilialIcms(this.empresaId, this.filialId, icms)
            .then(icms => this.onSave({ icms }))
            .catch(errors => this.onError(errors))
            .finally(() => this.apply())
    }

    updateFilialIcms(icms) {
        this.promise = this.parametrosTributarios
            .updateFilialIcms(this.empresaId, icms)
            .then(icms => this.onSave({ icms }))
            .catch(errors => this.onError(errors))
            .finally(() => this.apply())
    }

    onError(errors) {
        this.form.vigencia.$setValidity('overlap', !errors.some(e => e.key === 'vigencia'))
        this.onErrors({ errors: errors, icms: this.formatModel(this.model) })
    }
}

angular
    .module('eficienciaFiscalModule')
    .component('configuracaoIcms', {
        controller: ['gql', '$scope', 'ParametrosTributarios', ConfiguracaoIcms],
        bindings: {
            empresaId: '<',
            filialId: '<',
            icms: '<',
            onSave: '&',
            onErrors: '&',
            onChange: '&'
        },
        template: `
        <form name="$ctrl.form" ng-submit="$ctrl.submit()" novalidate>
            <div class="row">
                <loading promise="$ctrl.promise">
                    <div class="col-xs-12 form-group">
                        <label>Vigência</label>
                        <periodo-input 
                            ng-model="$ctrl.model.vigencia"
                            ng-change="$ctrl.onVigenciaChange()"
                            name="vigencia">
                        </periodo-input>
                        <div ng-messages="$ctrl.form.vigencia.$error" ng-if="$ctrl.form.$submitted">
                            <span 
                                class='help-block txt-color-red font-xs no-margin' 
                                ng-message="timeRange">A data final deve ser superior a data inicial.
                            </span>
                            <span 
                                class='help-block txt-color-red font-xs no-margin' 
                                ng-message="overlap">O período informado conflita com alguma outra configuração.
                            </span>
                        </div>
                    </div>
                    <div class="col-xs-12">
                        <label>Regime tributário</label>
                        <ui-select 
                            theme="bootstrap" 
                            name="regimeTributario" 
                            ng-model="$ctrl.model.regimeTributario"
                            required>
                            <ui-select-match placeholder="Clique aqui para selecionar um regime tributário.">
                                {{$select.selected.nome}}
                            </ui-select-match>
                            <ui-select-choices repeat="regime in $ctrl.regimesTributarios | filter: $select.search">
                                <div>{{regime.nome}}</div>
                            </ui-select-choices>
                        </ui-select>
                        <div ng-messages="$ctrl.form.regimeTributario.$error" ng-if="$ctrl.form.$submitted">
                            <span 
                                class='help-block txt-color-red font-xs no-margin' 
                                ng-message="required">Selecione um regime tributário.
                            </span>
                        </div>
                    </div>
                    <div class="col-xs-12">
                        <hr>
                        <div>
                            <label>Atividades</label>
                            <ui-select 
                                theme="bootstrap" 
                                name="atividade" 
                                ng-model="$ctrl.atividade">
                                <ui-select-match placeholder="Clique aqui para selecionar uma atividade.">
                                    {{$select.selected.descricao}}
                                </ui-select-match>
                                <ui-select-choices repeat="atividade in $ctrl.filteredAtividades | filter: $select.search">
                                    <div>{{atividade.descricao}}</div>
                                </ui-select-choices>
                            </ui-select>
                            <div ng-messages="$ctrl.form.atividade.$error" ng-if="$ctrl.form.$submitted">
                                <span 
                                    class='help-block txt-color-red font-xs no-margin' 
                                    ng-message="required">Selecione ao menos uma atividade.
                                </span>
                            </div>
                            
                            <ul class="list-group atividades-list repeat-animation" ng-if="$ctrl.model.atividades.length">
                                <li class="list-group-item repeat-animation" ng-repeat="atividade in $ctrl.model.atividades">
                                    <span>{{atividade.descricao}}</span>
                                    <i 
                                        class="fa fa-close text-danger cursor-pointer" 
                                        ng-click="$ctrl.removeAtividade(atividade)">
                                    </i>
                                </li>
                            </ul>
                        </div>
                        <hr ng-show="$ctrl.aplicacoes.length">
                        <div class="form-group slide-animation" ng-show="$ctrl.aplicacoes.length">
                            <label>Aplicações</label>
                            <div ng-repeat="aplicacao in $ctrl.aplicacoes" class="repeat-animation" style="padding: 3px 0;">
                                <label>
                                    <input  
                                        checklist-model="$ctrl.model.aplicacoes"
                                        checklist-value="aplicacao.id"
                                        type="checkbox" 
                                        class="checkbox" />
                                    <span>{{aplicacao.nome}}</span>
                                </label>
                            </div>
                        </div>
                        <hr ng-show="$ctrl.model.regimeTributario && $ctrl.filteredPerfis.length">
                        <div ng-if="$ctrl.model.regimeTributario && $ctrl.filteredPerfis.length" class="form-group repeat-animation">
                            <label>Perfis</label>
                            <ui-select 
                                theme="bootstrap" 
                                name="perfil" 
                                ng-model="$ctrl.perfil">
                                <ui-select-match placeholder="Clique aqui para selecionar um perfil.">
                                    {{$select.selected.nome}}
                                </ui-select-match>
                                <ui-select-choices repeat="perfil in $ctrl.filteredPerfis | filter: $select.search">
                                    <div>{{perfil.nome}}</div>
                                </ui-select-choices>
                            </ui-select>

                            <div class="well well-xs perfil repeat-animation" ng-repeat="perfil in $ctrl.model.perfis">
                                <div>
                                    <span>{{perfil.perfil.nome}}</span>
                                    <i class="fa fa-close cursor-pointer text-danger" ng-click="$ctrl.removePerfil(perfil)"></i>
                                </div>
                                <div class="row margin-top-10" ng-if="perfil.perfil.requerNumeroTermo">
                                    <div class="col-xs-6">
                                        <label>Número do termo</label>
                                        <input 
                                            class="form-control" 
                                            ng-model="perfil.configuracoes.numeroTermo"
                                            name="numeroTermo{{perfil.perfil.id}}"
                                            required>
                                        </input>
                                        <div ng-messages="$ctrl.form.numeroTermo{{perfil.perfil.id}}.$error" ng-if="$ctrl.form.$submitted">
                                            <span 
                                                class='help-block txt-color-red font-xs no-margin' 
                                                ng-message="required">Informe o número do termo.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div class="row margin-top-10" ng-if="perfil.perfil.requerCnpjFornecedores">
                                    <div class="col-xs-12">
                                        <label>CNPJ dos fornecedores</label>
                                        <div class="multi-cnpj">
                                            <div class="cnpj-list" ng-if="perfil.configuracoes.cnpjFornecedores.length">
                                                <div 
                                                    class="cnpj-tag" 
                                                    ng-repeat="cnpj in perfil.configuracoes.cnpjFornecedores">
                                                    {{cnpj | cpfCnpj}} <i class="fa fa-close" ng-click="$ctrl.removeCnpjFornecedor(perfil, cnpj)"></i>
                                                </div>
                                            </div>
                                            <input 
                                                class="form-control"
                                                placeholder="00.000.000/0000-00"
                                                name="cnpj{{perfil.perfil.id}}"
                                                ui-br-cnpj-mask
                                                ng-blur="$ctrl.addCnpjFornecedor(perfil)"
                                                ng-keydown="$ctrl.onCnpjKeyDown($event, perfil)"
                                                ng-model="perfil.cnpjFornecedor">
                                            </input>
                                        </div>
                                        <div ng-messages="$ctrl.form.cnpj{{perfil.perfil.id}}.$error">
                                            <span 
                                                class='help-block txt-color-red font-xs no-margin' 
                                                ng-message="cnpj">
                                                Informe pelo menos um CNPJ.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <hr>
                        <button class="pull-right btn btn-primary">Salvar</button>
                    </div>
                </loading>
            </div>
        </form>
        `
    })
